import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../../shared/context/UserContext';
import { useReferenceData } from '../../shared/hooks/useReferenceData';
import { ComboBox } from '../../shared/components/ComboBox';
import { getSites, createSite, updateSite } from './siteManagementApi';
import SupplierPicker from './components/SupplierPicker';
import './SiteManagementPage.css';

const STATE_OPTIONS = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
  'Alberta','British Columbia','Manitoba','New Brunswick',
  'Newfoundland and Labrador','Nova Scotia','Ontario',
  'Prince Edward Island','Quebec','Saskatchewan',
  'Aguascalientes','Baja California','Baja California Sur','Campeche',
  'Chiapas','Chihuahua','Coahuila','Colima','Durango','Guanajuato',
  'Guerrero','Hidalgo','Jalisco','Mexico City','Michoacán','Morelos',
  'Nayarit','Nuevo León','Oaxaca','Puebla','Querétaro','Quintana Roo',
  'San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas','Tlaxcala',
  'Veracruz','Yucatán','Zacatecas',
];

const COUNTRY_OPTIONS = [
  'United States','Canada','Mexico','China','Germany','Japan',
  'South Korea','United Kingdom','France','India','Brazil','Other',
];

// Display labels for site_modules values (raw value → Title Case label).
const MODULE_LABELS = {
  LIFECYCLE: 'Lifecycle',
  DISPOSAL:  'Disposal',
  WAREHOUSE: 'Warehouse',
};

// ---------------------------------------------------------------------------
// ModulesEditor — checkbox group bound to a site_modules array
// ---------------------------------------------------------------------------
function ModulesEditor({ options, selected, onChange }) {
  function toggle(value, checked) {
    if (checked) {
      if (!selected.includes(value)) onChange([...selected, value]);
    } else {
      onChange(selected.filter(v => v !== value));
    }
  }

  return (
    <div className="sm-modules">
      {(options || []).map(v => (
        <label key={v} className="sm-module-row">
          <input
            type="checkbox"
            className="sm-checkbox"
            checked={selected.includes(v)}
            onChange={e => toggle(v, e.target.checked)}
          />
          <span>{MODULE_LABELS[v] || v}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModulesReadonly — display enabled modules in read mode
// ---------------------------------------------------------------------------
function ModulesReadonly({ modules }) {
  if (!modules || modules.length === 0) {
    return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  }
  return (
    <div className="sm-modules-readonly">
      {modules.map(m => (
        <span key={m} className="sm-module-chip">{MODULE_LABELS[m] || m}</span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContactsEditor — inline sub-editor for the contacts array
// ---------------------------------------------------------------------------
function ContactsEditor({ contacts, onChange }) {
  function update(idx, field, value) {
    const next = contacts.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
    onChange(next);
  }

  function remove(idx) {
    onChange(contacts.filter((_, i) => i !== idx));
  }

  function add() {
    onChange([...contacts, { name: '', email: '', phone: '' }]);
  }

  return (
    <div className="sm-contacts">
      {contacts.map((c, i) => (
        <div key={i} className="sm-contact-row">
          <input
            type="text"
            value={c.name || ''}
            onChange={e => update(i, 'name', e.target.value)}
            placeholder="Name"
          />
          <input
            type="email"
            value={c.email || ''}
            onChange={e => update(i, 'email', e.target.value)}
            placeholder="Email"
          />
          <input
            type="tel"
            value={c.phone || ''}
            onChange={e => update(i, 'phone', e.target.value)}
            placeholder="Phone"
          />
          <button className="sm-contact-remove" onClick={() => remove(i)} title="Remove contact">
            ✕
          </button>
        </div>
      ))}
      <button className="sm-contact-add" onClick={add}>+ Add Contact</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContactsReadonly — display contacts in read mode
// ---------------------------------------------------------------------------
function ContactsReadonly({ contacts }) {
  if (!contacts || contacts.length === 0) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  return (
    <div className="sm-contacts-readonly">
      {contacts.map((c, i) => (
        <div key={i} className="sm-contact-chip">
          {c.name && <span className="sm-contact-name">{c.name} </span>}
          {c.email && <span>{c.email} </span>}
          {c.phone && <span>{c.phone}</span>}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DwellPolicyEditor — controlled editor for the nested dwell_policy object
//   { default_years: number|null, by_asset_type: { ASSET_TYPE: years } }
// Emits a COMPLETE, CLEAN object on EVERY change (manage_site PATCH replaces
// dwell_policy wholesale): default_years = parsed number or null; by_asset_type
// includes a key only when its row holds a finite number >= 0 (a blanked row
// drops the key — that's how a cleared threshold is removed). Local raw-string
// drafts keep partial input (e.g. "2.") usable without the cursor being fought.
// ---------------------------------------------------------------------------
function DwellPolicyEditor({ value, onChange, assetTypes }) {
  const v = value || { default_years: null, by_asset_type: {} };
  const [defaultRaw, setDefaultRaw] = useState(
    v.default_years == null ? '' : String(v.default_years)
  );
  const [drafts, setDrafts] = useState(() => {
    const init = {};
    Object.entries(v.by_asset_type || {}).forEach(([t, n]) => {
      init[t] = n == null ? '' : String(n);
    });
    return init;
  });

  // asset_type vocab is fetched async (useReferenceData) — backfill drafts for
  // any type that arrives after mount from the stored value, without disturbing
  // rows the user is already editing.
  useEffect(() => {
    setDrafts(prev => {
      const next = { ...prev };
      (assetTypes || []).forEach(t => {
        if (!(t in next)) {
          const n = (v.by_asset_type || {})[t];
          next[t] = n == null ? '' : String(n);
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(assetTypes || []).join('|')]);

  function emit(dRaw, draftsObj) {
    const dParsed = String(dRaw).trim() === '' ? null : Number(dRaw);
    const default_years = (dParsed !== null && Number.isFinite(dParsed)) ? dParsed : null;
    const by_asset_type = {};
    Object.entries(draftsObj).forEach(([t, raw]) => {
      const n = (raw == null || String(raw).trim() === '') ? null : Number(raw);
      if (n !== null && Number.isFinite(n) && n >= 0) by_asset_type[t] = n;
    });
    onChange({ default_years, by_asset_type });
  }

  function changeDefault(raw) { setDefaultRaw(raw); emit(raw, drafts); }
  function changeRow(t, raw) {
    const next = { ...drafts, [t]: raw };
    setDrafts(next);
    emit(defaultRaw, next);
  }

  return (
    <div className="sm-dwell-editor" style={{ marginTop: 6 }}>
      <label className="sm-dwell-row">
        <span className="sm-dwell-label">Default dwell (years)</span>
        <input
          type="text" inputMode="numeric"
          className="sm-inline-input sm-dwell-input"
          value={defaultRaw}
          onChange={e => changeDefault(e.target.value)}
          placeholder="—"
        />
      </label>
      {(assetTypes || []).map(t => (
        <label key={t} className="sm-dwell-row">
          <span className="sm-dwell-label">{t}</span>
          <input
            type="text" inputMode="numeric"
            className="sm-inline-input sm-dwell-input"
            value={drafts[t] ?? ''}
            onChange={e => changeRow(t, e.target.value)}
            placeholder="—"
          />
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SiteRow — click to edit, inline save/cancel
// ---------------------------------------------------------------------------
function SiteRow({ site, callerEmail, onSaved }) {
  const { options: refOpts } = useReferenceData(['oem', 'site_modules', 'asset_type'], callerEmail);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function startEdit() {
    setForm({
      site_name:           site.site_name || '',
      duns_number:         site.duns_number || '',
      oem_primary:         site.oem_primary || '',
      site_address:        site.site_address || '',
      site_zip_code:       site.site_zip_code || '',
      site_city:           site.site_city || '',
      site_state_province: site.site_state_province || '',
      site_country:        site.site_country || '',
      site_main_segment:   site.site_main_segment || '',
      active:              site.active !== false,
      contacts:            (site.contacts || []).map(c => ({ ...c })),
      site_modules:        Array.isArray(site.site_modules) ? [...site.site_modules] : [],
      warehouse_customer:  site.warehouse_customer || '',
      dwell_policy:        site.dwell_policy || { default_years: null, by_asset_type: {} },
    });
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
  }

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setError(null);
    try {
      setSaving(true);
      // Strip empty contacts
      const cleanContacts = form.contacts.filter(c => c.name || c.email || c.phone);
      await updateSite(callerEmail, site.site_id, { ...form, contacts: cleanContacts });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <>
        <tr className="sm-row-editing">
          {/* Site Name */}
          <td>
            <input
              type="text"
              value={form.site_name}
              onChange={e => set('site_name', e.target.value)}
              className="sm-inline-input"
              autoFocus
            />
            <input
              type="text"
              value={form.duns_number}
              onChange={e => set('duns_number', e.target.value)}
              className="sm-inline-input"
              placeholder="DUNS Number"
              style={{ marginTop: 4 }}
            />
            <div className="sm-site-code">{site.supplier_site_code}</div>
          </td>
          {/* OEM */}
          <td>
            <select value={form.oem_primary} onChange={e => set('oem_primary', e.target.value)}
              className="sm-inline-input">
              <option value="">Select OEM…</option>
              {(refOpts.oem || []).map(v => <option key={v} value={v}>{v}</option>)}
              {form.oem_primary && !(refOpts.oem || []).includes(form.oem_primary) && (
                <option value={form.oem_primary}>{form.oem_primary}</option>
              )}
            </select>
          </td>
          {/* Address */}
          <td>
            <input type="text" value={form.site_address}        onChange={e => set('site_address', e.target.value)}        className="sm-inline-input" placeholder="Street" />
            <input type="text" value={form.site_city}            onChange={e => set('site_city', e.target.value)}            className="sm-inline-input" placeholder="City" style={{ marginTop: 4 }} />
            <input type="text" value={form.site_state_province}  onChange={e => set('site_state_province', e.target.value)}  className="sm-inline-input" placeholder="State/Province" style={{ marginTop: 4 }} />
            <input type="text" value={form.site_zip_code}        onChange={e => set('site_zip_code', e.target.value)}        className="sm-inline-input" placeholder="Zip" style={{ marginTop: 4 }} />
            <input type="text" value={form.site_country}         onChange={e => set('site_country', e.target.value)}         className="sm-inline-input" placeholder="Country" style={{ marginTop: 4 }} />
          </td>
          {/* Segment */}
          <td>
            <input type="text" value={form.site_main_segment} onChange={e => set('site_main_segment', e.target.value)} className="sm-inline-input" placeholder="Segment" />
          </td>
          {/* Contacts */}
          <td>
            <ContactsEditor contacts={form.contacts} onChange={c => set('contacts', c)} />
          </td>
          {/* Modules */}
          <td>
            <ModulesEditor
              options={refOpts.site_modules || []}
              selected={form.site_modules}
              onChange={m => set('site_modules', m)}
            />
            {form.site_modules.includes('WAREHOUSE') && (
              <>
                <input
                  type="text"
                  value={form.warehouse_customer || ''}
                  onChange={e => set('warehouse_customer', e.target.value)}
                  className="sm-inline-input"
                  placeholder="Warehouse customer"
                  style={{ marginTop: 6 }}
                />
                <DwellPolicyEditor
                  value={form.dwell_policy}
                  onChange={v => set('dwell_policy', v)}
                  assetTypes={refOpts.asset_type || []}
                />
              </>
            )}
          </td>
          {/* Active */}
          <td>
            <input
              type="checkbox"
              checked={form.active}
              onChange={e => set('active', e.target.checked)}
              className="sm-checkbox"
            />
          </td>
          {/* Actions */}
          <td>
            <div className="sm-inline-actions">
              <button className="sm-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? '...' : 'Save'}
              </button>
              <button className="sm-cancel-btn" onClick={cancelEdit} disabled={saving}>
                Cancel
              </button>
            </div>
          </td>
        </tr>
        {error && (
          <tr className="sm-row-error">
            <td colSpan={8}><div className="sm-inline-error">{error}</div></td>
          </tr>
        )}
      </>
    );
  }

  // ── Read-only row ──
  return (
    <tr
      className={`sm-row ${site.active === false ? 'sm-row-inactive' : ''}`}
      onClick={startEdit}
      title="Click to edit"
    >
      <td>
        <div className="sm-site-name">{site.site_name || '—'}</div>
        <div className="sm-site-code">{site.supplier_site_code}</div>
      </td>
      <td className="sm-oem">{site.oem_primary || '—'}</td>
      <td>
        {site.site_address || site.site_city ? (
          <>
            {site.site_address && <div>{site.site_address}</div>}
            <div>
              {[site.site_city, site.site_state_province, site.site_zip_code]
                .filter(Boolean)
                .join(', ')}
            </div>
            {site.site_country && <div>{site.site_country}</div>}
          </>
        ) : '—'}
      </td>
      <td>{site.site_main_segment || '—'}</td>
      <td>
        <ContactsReadonly contacts={site.contacts} />
      </td>
      <td>
        <ModulesReadonly modules={site.site_modules} />
      </td>
      <td>
        <span className={`sm-status-badge ${site.active !== false ? 'sm-active' : 'sm-inactive'}`}>
          {site.active !== false ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="sm-col-actions">
        <span className="sm-edit-hint">Edit</span>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// AddSiteRow — inline new site form
// ---------------------------------------------------------------------------
function AddSiteRow({ callerEmail, onSaved, onCancel }) {
  const { options: refOpts } = useReferenceData(['oem', 'site_modules', 'asset_type'], callerEmail);
  const [form, setForm] = useState({
    site_name: '', duns_number: '', supplier_id: '', oem_primary: '', site_address: '',
    site_city: '', site_state_province: '', site_country: '',
    site_main_segment: '', contacts: [], site_modules: [], warehouse_customer: '',
    dwell_policy: { default_years: null, by_asset_type: {} },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setTouched(true);
    setFieldErrors(prev => ({ ...prev, [field]: null }));
  }

  async function handleSave() {
    setError(null);
    const errs = {};
    if (!form.site_name.trim()) errs.site_name = true;
    if (!form.supplier_id.trim()) errs.supplier_id = true;
    if (!form.oem_primary) errs.oem_primary = true;
    if (!form.site_city.trim()) errs.site_city = true;
    if (!form.site_state_province.trim()) errs.site_state_province = true;
    if (!form.site_country.trim()) errs.site_country = true;
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError('All highlighted fields are required.');
      return;
    }
    try {
      setSaving(true);
      await createSite(callerEmail, form);
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (touched && !window.confirm('Discard unsaved site?')) return;
    onCancel();
  }

  const errCls = (field) => fieldErrors[field] ? 'sm-inline-input sm-input-error' : 'sm-inline-input';

  return (
    <>
      <tr className="sm-row-editing">
        <td>
          <input type="text" value={form.site_name} onChange={e => set('site_name', e.target.value)}
            className={errCls('site_name')} placeholder="Site name" autoFocus />
          <input type="text" value={form.duns_number} onChange={e => set('duns_number', e.target.value)}
            className="sm-inline-input" placeholder="DUNS Number" style={{ marginTop: 4 }} />
          <div style={{ marginTop: 4 }}>
            <SupplierPicker
              value={form.supplier_id}
              onChange={(id) => set('supplier_id', id)}
              callerEmail={callerEmail}
              className={errCls('supplier_id')}
            />
          </div>
        </td>
        <td>
          <select value={form.oem_primary} onChange={e => set('oem_primary', e.target.value)}
            className={fieldErrors.oem_primary ? 'sm-inline-input sm-input-error' : 'sm-inline-input'}>
            <option value="">Select OEM…</option>
            {(refOpts.oem || []).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </td>
        <td>
          <input type="text" value={form.site_address} onChange={e => set('site_address', e.target.value)}
            className="sm-inline-input" placeholder="Street" />
          <input type="text" value={form.site_city} onChange={e => set('site_city', e.target.value)}
            className={errCls('site_city')} placeholder="City" style={{ marginTop: 4 }} />
          <div style={{ marginTop: 4 }}>
            <ComboBox value={form.site_state_province} onChange={v => set('site_state_province', v)}
              options={STATE_OPTIONS} placeholder="State/Province"
              className={errCls('site_state_province')} />
          </div>
          <div style={{ marginTop: 4 }}>
            <ComboBox value={form.site_country} onChange={v => set('site_country', v)}
              options={COUNTRY_OPTIONS} placeholder="Country"
              className={errCls('site_country')} />
          </div>
        </td>
        <td>
          <input
            type="text"
            value={form.site_main_segment}
            onChange={e => set('site_main_segment', e.target.value)}
            className="sm-inline-input"
            placeholder="Segment"
          />
        </td>
        <td>
          <ContactsEditor
            contacts={form.contacts}
            onChange={rows => { setForm(prev => ({ ...prev, contacts: rows })); setTouched(true); }}
          />
        </td>
        <td>
          <ModulesEditor
            options={refOpts.site_modules || []}
            selected={form.site_modules}
            onChange={m => set('site_modules', m)}
          />
          {form.site_modules.includes('WAREHOUSE') && (
            <>
              <input
                type="text"
                value={form.warehouse_customer || ''}
                onChange={e => set('warehouse_customer', e.target.value)}
                className="sm-inline-input"
                placeholder="Warehouse customer"
                style={{ marginTop: 6 }}
              />
              <DwellPolicyEditor
                value={form.dwell_policy}
                onChange={v => set('dwell_policy', v)}
                assetTypes={refOpts.asset_type || []}
              />
            </>
          )}
        </td>
        <td>
          <span className="sm-status-badge sm-active">Active</span>
        </td>
        <td>
          <div className="sm-inline-actions">
            <button className="sm-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? '...' : 'Save'}
            </button>
            <button className="sm-cancel-btn" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </td>
      </tr>
      {error && (
        <tr className="sm-row-error">
          <td colSpan={8}><div className="sm-inline-error">{error}</div></td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// SiteManagementPage
// ---------------------------------------------------------------------------
export default function SiteManagementPage() {
  const { callerEmail } = useUser();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingSite, setAddingSite] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  const loadSites = useCallback(async () => {
    if (!callerEmail) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getSites(callerEmail);
      setSites(data);
    } catch (err) {
      setError('Failed to load sites.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [callerEmail]);

  useEffect(() => {
    if (callerEmail) loadSites();
  }, [callerEmail, loadSites]);

  // Client-side filtering
  const filtered = sites.filter(s => {
    if (filterActive === 'active' && s.active === false) return false;
    if (filterActive === 'inactive' && s.active !== false) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      return (
        (s.site_name || '').toLowerCase().includes(q) ||
        (s.supplier_site_code || '').toLowerCase().includes(q) ||
        (s.site_city || '').toLowerCase().includes(q) ||
        (s.oem_primary || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleExport = useCallback(() => {
    const rows = filtered.map(s => ({
      'Site Name':          s.site_name || '',
      'Supplier Site Code': s.supplier_site_code || '',
      'City':               s.site_city || '',
      'State':              s.site_state_province || '',
      'OEM':                s.oem_primary || '',
      'Segment':            s.site_main_segment || '',
      'Status':             s.active === false ? 'Inactive' : 'Active',
      'Contacts':           Array.isArray(s.contacts) ? s.contacts.length : 0,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sites');
    // toLocaleDateString('en-CA') yields YYYY-MM-DD in local time — standing
    // instructions forbid toISOString().slice(0,10) due to ET timezone drift.
    const today = new Date().toLocaleDateString('en-CA');
    XLSX.writeFile(wb, `ToolSight_Sites_${today}.xlsx`);
  }, [filtered]);

  return (
    <div className="sm-page">
      <header className="sm-header">
        <div className="sm-header-left">
          <h1>SITE MANAGEMENT</h1>
          <span className="sm-badge">ADMIN</span>
        </div>
        <div className="sm-header-right">
          <span className="sm-count">{filtered.length} site{filtered.length !== 1 ? 's' : ''}</span>
          <button className="sm-add-btn" onClick={handleExport} disabled={filtered.length === 0}>Export</button>
          {!addingSite && (
            <button className="sm-add-btn" onClick={() => setAddingSite(true)}>+ Add Site</button>
          )}
        </div>
      </header>

      <div className="sm-filters">
        <input
          type="text"
          placeholder="Search name, code, city, OEM..."
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          className="sm-search"
        />
        <select value={filterActive} onChange={e => setFilterActive(e.target.value)} className="sm-select">
          <option value="all">ALL</option>
          <option value="active">ACTIVE</option>
          <option value="inactive">INACTIVE</option>
        </select>
      </div>

      <div className="sm-table-wrap">
        {loading ? (
          <div className="sm-state">Loading sites...</div>
        ) : error ? (
          <div className="sm-state sm-state-error">{error}</div>
        ) : (
          <table className="sm-table">
            <thead>
              <tr>
                <th>SITE</th>
                <th>OEM</th>
                <th>ADDRESS</th>
                <th>SEGMENT</th>
                <th>CONTACTS</th>
                <th>MODULES</th>
                <th>STATUS</th>
                <th className="sm-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {addingSite && (
                <AddSiteRow
                  callerEmail={callerEmail}
                  onSaved={() => { setAddingSite(false); loadSites(); }}
                  onCancel={() => setAddingSite(false)}
                />
              )}
              {filtered.length === 0 && !addingSite ? (
                <tr><td colSpan={8} className="sm-state">No sites match your filters.</td></tr>
              ) : (
                filtered.map(site => (
                  <SiteRow
                    key={site.site_id}
                    site={site}
                    callerEmail={callerEmail}
                    onSaved={loadSites}
                  />
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
